// Loan maths shared by the screens and the mock (LOANS spec §4–§10).
// All money in paise unless a name says Rupees; rates in percent.
import type { LoanProduct, LoanRecord, ScheduleEntry } from '@/types/loan.types';

export const GST_PCT = 18;
export const REPO_RATE_PCT = 6.5;
export const MS_DAY = 86_400_000;

/** Reducing-balance EMI in paise. Zero rate → straight division. */
export const emiFor = (principalPaise: number, monthlyRatePct: number, months: number): number => {
  if (months <= 0) return principalPaise;
  if (monthlyRatePct <= 0) return Math.round(principalPaise / months);
  const r = monthlyRatePct / 100;
  const factor = Math.pow(1 + r, months);
  return Math.round((principalPaise * r * factor) / (factor - 1));
};

/** Full amortisation schedule from a disbursement date and EMI day (5/10/15). */
export const buildSchedule = (
  principalPaise: number,
  monthlyRatePct: number,
  months: number,
  firstDueAt: number
): ScheduleEntry[] => {
  const emi = emiFor(principalPaise, monthlyRatePct, months);
  const entries: ScheduleEntry[] = [];
  let balance = principalPaise;
  let due = firstDueAt;
  for (let n = 1; n <= months; n += 1) {
    const interest = Math.round(balance * (monthlyRatePct / 100));
    let principal = emi - interest;
    if (n === months) principal = balance; // last instalment clears the residue
    const payment = interest + principal;
    balance = Math.max(0, balance - principal);
    entries.push({ n, dueDate: due, emiPaise: payment, interestPaise: interest, principalPaise: principal, balanceAfterPaise: balance });
    due = addMonths(due, 1);
  }
  return entries;
};

/** Monthly IRR of (takeHome now, then each EMI), annualised ×12 — spec §5 L10. */
export const aprPct = (takeHomePaise: number, paymentsPaise: number[]): number => {
  if (takeHomePaise <= 0 || paymentsPaise.length === 0) return 0;
  const npv = (r: number): number => {
    let v = -takeHomePaise;
    paymentsPaise.forEach((p, i) => {
      v += p / Math.pow(1 + r, i + 1);
    });
    return v;
  };
  let lo = 0;
  let hi = 1; // 100% a month is far above any real case
  for (let i = 0; i < 80; i += 1) {
    const mid = (lo + hi) / 2;
    if (npv(mid) > 0) lo = mid;
    else hi = mid;
  }
  return (lo + hi) * 6 * 100; // ((lo+hi)/2)*12*100
};

export const addMonths = (ts: number, months: number): number => {
  const d = new Date(ts);
  d.setMonth(d.getMonth() + months);
  return d.getTime();
};

/** Next due date on the given day-of-month, at or after `from` (always future). */
export const nextEmiDate = (from: number, emiDay: number): number => {
  const d = new Date(from);
  d.setDate(emiDay);
  d.setHours(9, 0, 0, 0);
  if (d.getTime() <= from) d.setMonth(d.getMonth() + 1);
  return d.getTime();
};

/** Cooling-off days: 1 when the tenure is under 7 days, else 3 (spec §5 L2). */
export const coolingOffDays = (tenureDays: number): number => (tenureDays < 7 ? 1 : 3);

export const tenureInDays = (months?: number, days?: number): number =>
  days ?? (months ?? 12) * 30;

// ---------- fees ----------

export const feeFor = (product: LoanProduct, amountPaise: number): number => {
  if (product.fee.kind === 'NONE') return 0;
  if (product.fee.kind === 'FLAT') return Math.round((product.fee.flatRupees ?? 0) * 100);
  const pct = Math.round((amountPaise * (product.fee.pct ?? 0)) / 100);
  const cap = product.fee.maxRupees ? product.fee.maxRupees * 100 : Number.MAX_SAFE_INTEGER;
  return Math.min(pct, cap);
};

export const gstOn = (paise: number): number => Math.round((paise * GST_PCT) / 100);

// ---------- Personal / Short-Term tiers (§6.3) ----------

export interface InstantTier {
  minPaise: number;
  maxPaise: number;
  tenures: number[];
  feePct: number;
  monthlyRatePct: number;
  insurancePct?: number;
}

export const instantTiers = (limitPaise: number): InstantTier[] => [
  { minPaise: 1_000 * 100, maxPaise: 19_999_99, tenures: [1], feePct: 7.5, monthlyRatePct: 0 },
  { minPaise: 20_000 * 100, maxPaise: Math.floor(limitPaise / 2), tenures: [1, 3, 6], feePct: 6, monthlyRatePct: 2, insurancePct: 0.13 },
  { minPaise: Math.floor(limitPaise / 2) + 1, maxPaise: limitPaise, tenures: [3, 6], feePct: 6, monthlyRatePct: 2, insurancePct: 0.28 },
];

export const tierFor = (limitPaise: number, amountPaise: number): InstantTier | undefined =>
  instantTiers(limitPaise).find((t) => amountPaise >= t.minPaise && amountPaise <= t.maxPaise);

// ---------- LTV rules ----------

/** Home (§9): 90% ≤ ₹30L, 80% ≤ ₹75L, 75% above. */
export const homeLtvPct = (amountPaise: number): number => {
  if (amountPaise <= 30_00_000 * 100) return 90;
  if (amountPaise <= 75_00_000 * 100) return 80;
  return 75;
};

/** Gold LTV tiers (§9.1): 85% ≤ ₹2.5L, 80% ≤ ₹5L, 75% above. */
export const goldLtvPct = (amountPaise: number): number => {
  if (amountPaise <= 2_50_000 * 100) return 85;
  if (amountPaise <= 5_00_000 * 100) return 80;
  return 75;
};

// ---------- running-loan selectors ----------

export const isTermEmiLoan = (loan: LoanRecord): boolean =>
  !loan.bulletDueDate &&
  loan.schedule.length > 0 &&
  ['active', 'overdue', 'moratorium'].includes(loan.state);

export const isRevolving = (loan: LoanRecord): boolean =>
  ['WORKING_CAPITAL', 'OVERDRAFT', 'LAMF', 'LAS', 'PAYLATER'].includes(loan.productId);

/** Days held for cooling-off maths, from the bank clock. */
export const simpleInterestForDays = (principalPaise: number, ratePaPct: number, days: number): number =>
  Math.round((principalPaise * (ratePaPct / 100) * days) / 365);

export const paidEmis = (loan: LoanRecord): number => loan.schedule.filter((s) => s.paidAt).length;

export const nextUnpaid = (loan: LoanRecord): ScheduleEntry | undefined =>
  loan.schedule.find((s) => !s.paidAt);

/** "05-Nov-26" review-screen date. */
export const formatReviewDate = (ts: number): string => {
  const d = new Date(ts);
  return `${String(d.getDate()).padStart(2, '0')}-${d.toLocaleString('en-IN', { month: 'short' })}-${String(d.getFullYear()).slice(2)}`;
};

/** "5 Nov 2026" loan-screen date. */
export const formatLoanDate = (ts: number): string => {
  const d = new Date(ts);
  return `${d.getDate()} ${d.toLocaleString('en-IN', { month: 'short' })} ${d.getFullYear()}`;
};
